import { AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios';
import { http } from 'SharedDir/backend/axios/http';

import { ResponseTypes } from 'SharedDir/constants/response-type';
import { Paginated, PaginationParams } from 'SharedDir/declarations/pagination';
import { QuasarTableExportColumns } from 'SharedDir/declarations/shared';

export interface SimpleApiInterface<
  TSingle,
  TStore,
  TUpdate = TStore,
  TPaginated = TSingle,
  TStoreResult = TSingle,
  TUpdateResult = TSingle,
  TDeleteResult = TSingle
> {
  getPaginated<Extra extends object>(
    params: PaginationParams<Extra>
  ): Promise<Paginated<TPaginated, Extra>>;
  getAll<Extra extends object>(
    params: Omit<PaginationParams<object>, 'rowsPerPage' | 'page'> & Extra
  ): Promise<TPaginated[]>;
  get(...args: unknown[]): Promise<TSingle>;
  store(...args: unknown[]): Promise<TStoreResult>;
  update(...args: unknown[]): Promise<TUpdateResult>;
  delete(...args: unknown[]): Promise<TDeleteResult>;
  exportTable<Extra extends object>(
    params: PaginationParams<Extra>,
    data: QuasarTableExportColumns,
    responseType?: ResponseTypes
  ): AxiosPromise;
  exportTableAll<Extra extends object>(
    params: Omit<PaginationParams<object>, 'rowsPerPage' | 'page'> & Extra,
    data: QuasarTableExportColumns,
    responseType?: ResponseTypes
  ): AxiosPromise;
}

/**
 * Класс для использования при взаимодействии с бекэндом по REST.
 *
 * Доступные методы:
 *
 * - просмотр списка элементов (постранично) - {@link getPaginated} - _GET_
 * - запрос элемента по идентификатору  - {@link get} - _GET_
 * - создание элемента - {@link store} - _POST_
 * - редактирование элемента по идентификатору - {@link update} - _PUT_
 * - удаление элемента по идентификатору - {@link delete} - _DELETE_
 *
 * При вызове метода из списка выше будет отправлен запрос по адресу указанному при инициализации класса, с соответствующим методом.
 * В добавок если для выполнения нужен идентификатор элемента то он будет &laquo;приклеен&raquo; к адресу.
 *
 * Пример:
 * ```ts
 * interface BookIn {
 *   name: string;
 *   author: string;
 * }
 *
 * interface Book extends BookIn {
 *   id: number;
 * }
 *
 * const api = new SimpleApi<Book, BookIn>('https://shop.books.com/api/v1/books');
 *
 * // POST запрос по адресу https://shop.books.com/api/v1/books
 * const newBook = await api.store({name: 'Смешные анекдоты', author: 'Роман Трехтенберг'});
 *
 * // GET запрос по адресу https://shop.books.com/api/v1/books/1
 * const getBook = await api.get(1);
 * ```
 *
 * Generic Types:
 *  - `TSingle`* - Тип который возвращает метод {@link get}.
 *  - `TStore`* - Тип который принимает метод {@link store}.
 *  - `TUpdate` - Тип который принимает метод {@link update}, по-умолчанию = `TStore`.
 *  - `TPaginated` - Тип который возвращает метод {@link getPaginated} по-умолчанию = `TSingle`.
 *  - `TStoreResult` - Тип который возвращает метод {@link store} по-умолчанию = `TSingle`.
 *  - `TUpdateResult` - Тип который возвращает метод {@link update} по-умолчанию = `TSingle`.
 *  - `TDeleteResult` - Тип который возвращает метод {@link delete} по-умолчанию = `TSingle`.
 *
 * @param url - адрес для отправки запросов
 */
export default class SimpleApi<
  TSingle,
  TStore,
  TUpdate = TStore,
  TPaginated = TSingle,
  TStoreResult = TSingle,
  TUpdateResult = TSingle,
  TDeleteResult = TSingle
> implements
    SimpleApiInterface<
      TSingle,
      TStore,
      TUpdate,
      TPaginated,
      TStoreResult,
      TUpdateResult,
      TDeleteResult
    > {
  protected readonly url: string;

  /**
   * @param url - адрес для отправки запросов
   */
  constructor(url: string) {
    this.url = url;
  }

  /**
   * Постраничный запрос элемнатов
   * @param params
   */
  public async getPaginated<Extra extends object>(params: PaginationParams<Extra>) {
    const endpoint = this.getPaginationEndpoint();
    const { data } = (await http({ ...endpoint, params })) as AxiosResponse<
      Paginated<TPaginated, Extra>
    >;
    return data;
  }

  /**
   * Запрашиваем элемент по идентификатору.
   *
   * Поддерживаются как числовые так и текстовые идентификаторы
   * @param id
   * @param params
   */
  public async get(id: string | number, params?: Record<string, unknown>) {
    const endpoint = this.getSingleEndpoint(id);
    const { data } = (await http({ ...endpoint, params })) as AxiosResponse<TSingle>;

    return data;
  }

  /**
   * Сохраняем новый элемент
   * @param data
   */
  public async store(data: TStore) {
    const { data: saved } = (await http({
      ...this.getStoreEndpoint(),
      data,
    })) as AxiosResponse<TStoreResult>;

    return saved;
  }

  /**
   * Сохраняем существующий элемент
   * @param id
   * @param data
   */
  public async update(id: string | number, data: TUpdate) {
    const { data: updated } = (await http({
      ...this.getUpdateEndpoint(id),
      data,
    })) as AxiosResponse<TUpdateResult>;

    return updated;
  }

  /**
   * Удаляем запись
   * @param id
   */
  public async delete(id: string | number) {
    const { data: deleted } = (await http(
      this.getDeleteEndpoint(id)
    )) as AxiosResponse<TDeleteResult>;

    return deleted;
  }

  public async exportTable<Extra extends object>(
    params: PaginationParams<Extra>,
    data: QuasarTableExportColumns,
    responseType: ResponseTypes = 'blob'
  ) {
    const endpoint = this.getExportEndpoint();
    const responseData = http({
      responseType,
      ...endpoint,
      params,
      data,
    }) as AxiosPromise<AxiosResponse>;

    return responseData;
  }

  public async exportTableAll<Extra extends object>(
    params: Omit<PaginationParams<object>, 'rowsPerPage' | 'page'> & Extra,
    data: QuasarTableExportColumns,
    responseType: ResponseTypes = 'blob'
  ): Promise<AxiosResponse> {
    return this.exportTable({ ...params, rowsPerPage: 0, page: 1 }, data, responseType);
  }

  protected getPaginationEndpoint(): AxiosRequestConfig {
    return { url: this.url, method: 'get' };
  }

  private getStoreEndpoint(): AxiosRequestConfig {
    return { url: this.url, method: 'post' };
  }

  protected getSingleEndpoint(id: string | number): AxiosRequestConfig {
    const url = `${this.url}/${id}`;
    return { url, method: 'get' };
  }

  private getUpdateEndpoint(id: string | number): AxiosRequestConfig {
    const url = `${this.url}/${id}`;
    return { url, method: 'put' };
  }

  private getDeleteEndpoint(id: string | number): AxiosRequestConfig {
    const url = `${this.url}/${id}`;
    return { url, method: 'delete' };
  }

  protected getExportEndpoint(): AxiosRequestConfig {
    const url = `${this.url}/export`;
    return { url, method: 'post' };
  }

  /**
   * Запрашиваем все!!!! записи
   *
   * @param {Omit<PaginationParams<Extra>, "rowsPerPage" | "page">} params
   * @returns {Promise<any>}
   */
  public async getAll<Extra extends object>(
    params: Omit<PaginationParams<object>, 'rowsPerPage' | 'page'> & Extra
  ) {
    const { data } = await this.getPaginated({ ...params, rowsPerPage: 0, page: 1 });
    return data;
  }
}
